<template>
    <v-responsive :aspect-ratio="aspectRatio" :height="height" class="breddys-video-responsive">
        <v-container fluid pa-0 class="jwplayer-layer" :style="playerOffset">
            <div :style="videoContainerStyle" @click="onPlayerClick" class="jwplayer-player">
                <div :id="playerID"></div>
            </div>
        </v-container>
        <v-layout row wrap align-center class="player-caption" v-if="caption && displayCaption">
            <v-flex xs12 text-xs-center white--text class="player-caption-data">
                <div>
                    <slot name="caption">
                        <div class="display-1 mb-4">
                            {{ caption | translation }}
                        </div>
                    </slot>
                    <slot name="button">
                        <v-btn color="secondary" depressed :style="buttonStyle" class="video-button">
                            <v-icon class="pr-2" size="38">play_circle_filled</v-icon>
                            {{ buttonText | translation }}
                        </v-btn>
                    </slot>
                </div>
            </v-flex>
            <v-img :src="previewImage" v-if="!videoStarted" width="100%" height="100%" class="preview-image" />
        </v-layout>
    </v-responsive>
</template>

<script>
export default {
    props: {
        apiKey: String,
        videoLink: String,
        previewImage: String,
        aspectRatio: {
            type: Number,
            default: 1,
        },
        desktopHeight: Number,
        mobileHeight: Number,
        autoplay: {
            type: Boolean,
            default: false,
        },
        loop: {
            type: Boolean,
            default: false,
        },
        mute: {
            type: Boolean,
            default: false,
        },
        caption: String,
        buttonText: String,
        captionWhenPlayed: Boolean,
        pauseable: {
            default: true,
            type: Boolean,
        },
    },
    data() {
        return {
            maxTries: 4,
            tries: 0,
            interval: null,
            displayCaption: false,
            player: null,
            videoContainerStyle: {
                height: '100%',
                'max-width': 'unset',
                width: '100%',
            },
            playerOffset: {
                left: 0,
            },
            videoStarted: false,
            play: false,
        };
    },
    created() {
        if (this.apiKey && !document.getElementById('jwplayerScript')) {
            const script = document.createElement('script');
            script.src = `https://cdn.jwplayer.com/libraries/${this.apiKey}.js?hash=${this.getRandomString()}`;
            script.id = 'jwplayerScript';
            script.setAttribute('type', 'text/javascript');
            document
                .getElementsByTagName('script')[0]
                .parentNode.insertBefore(script, document.getElementsByTagName('script')[0]);
        }
    },
    mounted() {
        let playerInitialized = false;
        this.interval = setInterval(() => {
            if (window.jwplayer) {
                this.setupPlayer();
                playerInitialized = true;
            }
            if (playerInitialized || this.tries >= this.maxTries) {
                clearInterval(this.interval);
            }

            this.tries++;
        }, 500);
        if (this.caption) {
            this.displayCaption = true;
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    computed: {
        height() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return this.mobileHeight;
            } else {
                return this.desktopHeight;
            }
        },
        playerID() {
            return `jwplayer-${this._uid}`;
        },
        buttonStyle() {
            return {
                width: '343px',
                height: '75px',
            };
        },
    },
    methods: {
        setupPlayer(dropInstance = false) {
            const settings = this.getSettings();
            if (dropInstance) {
                window.jwplayer(this.playerID).remove();
            }
            this.$nextTick(() => {
                window.jwplayer(this.playerID).setup(settings);
                window.jwplayer(this.playerID).on('ready', () => {
                    this.onResize();
                    window.addEventListener('resize', this.onResize);
                    if (this.autoplay) {
                        this.play = true;
                        if (!this.captionWhenPlayed) {
                            this.displayCaption = false;
                        }
                    }
                });
                window.jwplayer(this.playerID).on('complete', () => {
                    if (!this.autoplay) {
                        this.displayCaption = !this.displayCaption;
                    } else if (!this.captionWhenPlayed) {
                        this.displayCaption = false;
                    }
                });
                window.jwplayer(this.playerID).on('error', () => {
                    this.setupPlayer(true);
                });
            });
        },
        getRandomString() {
            return (
                new Date().getTime() +
                Math.random()
                    .toString(36)
                    .substring(7)
            );
        },
        getSettings() {
            return {
                file: this.videoLink,
                repeat: this.loop,
                controls: false,
                mute: this.mute,
            };
        },
        onPlayerClick() {
            if (window.jwplayer && this.pauseable) {
                this.play = !this.play;
            }
            if (!this.captionWhenPlayed) {
                this.displayCaption = !this.displayCaption;
            }
        },
        onResize() {
            const size = this.$el.parentElement.offsetHeight * this.aspectRatio;

            this.videoContainerStyle.height = `${this.height}px`;
            this.videoContainerStyle.width = `${size}px`;

            const offset = size - this.$el.parentElement.offsetWidth;
            if (size > this.$el.parentElement.offsetWidth) {
                this.playerOffset.left = `-${offset / 2}px`;
            } else {
                this.playerOffset.left = 0;
            }
            if (window.jwplayer) {
                window.jwplayer(this.playerID).resize(size, this.height);
            }
        },
    },
    watch: {
        play(play) {
            if (play) {
                window.jwplayer(this.playerID).play();
                this.videoStarted = play;
            } else {
                window.jwplayer(this.playerID).pause();
            }
        },
    },
};
</script>

<style scoped>
.player-caption {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
}
.jwplayer-layer {
    position: relative;
}
.jwplayer-player {
    margin: 0 auto;
}
.breddys-video-container {
    margin: 0 !important;
}
.breddys-video-responsive {
    background: #000;
}
.breddys-video-responsive .v-responsive__content {
    max-width: 100%;
}
.video-button {
    text-transform: unset;
    font-size: 23px !important;
    letter-spacing: 0px;
    border-radius: 5px;
}
.player-caption-data {
    z-index: 1;
}
.preview-image {
    position: absolute;
}
</style>

<style>
.player-caption .clickable {
    pointer-events: all;
}
</style>
